import React from "react";
import {
  AbsoluteFill,
  Audio,
  interpolate,
  Sequence,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { ThreeCanvas } from "@remotion/three";
import * as THREE from "three";

// --- PROCEDURAL 3D ZUI (SPEAKER 1) ---
const ZuiMesh: React.FC<{
  frame: number;
  fps: number;
  isSpeaking: boolean;
}> = ({ frame, fps, isSpeaking }) => {
  const breathe = Math.sin(frame * 0.1) * 0.035;
  const bobY = Math.sin(frame * 0.12) * 0.04;
  const earTwitchLeft = Math.sin(frame * 0.18) * 0.08;
  const earTwitchRight = Math.cos(frame * 0.14) * 0.06;
  const tailWag = Math.sin(frame * 0.22) * 0.5;
  const wave = Math.sin(frame * 0.35) * 0.45;

  // Real-time lip sync animation
  const mouthOpen = isSpeaking ? (0.5 + 0.5 * Math.sin(frame * 0.6)) : 0;

  // Blinking logic
  const blinkCycle = frame % 90;
  const isBlinking = blinkCycle > 82 && blinkCycle < 88;
  const eyeScaleY = isBlinking ? 0.08 : 1.0;

  // PBR Materials
  const furMaterial = new THREE.MeshStandardMaterial({
    color: "#FFFBF5",
    roughness: 0.32,
    metalness: 0.04,
  });
  const bellyMaterial = new THREE.MeshStandardMaterial({
    color: "#FFE7D1",
    roughness: 0.4,
    metalness: 0.02,
  });
  const innerEarMaterial = new THREE.MeshStandardMaterial({
    color: "#FFA8BA",
    roughness: 0.35,
    metalness: 0.0,
  });
  const eyeGlossMaterial = new THREE.MeshStandardMaterial({
    color: "#080512",
    roughness: 0.02,
    metalness: 0.25,
  });
  const eyeHighlightMaterial = new THREE.MeshStandardMaterial({
    color: "#FFFFFF",
    roughness: 0.08,
    metalness: 0.0,
  });
  const blushMaterial = new THREE.MeshStandardMaterial({
    color: "#FF5E7E",
    emissive: new THREE.Color("#FF2255"),
    emissiveIntensity: 0.35,
    roughness: 0.4,
  });
  const noseMaterial = new THREE.MeshStandardMaterial({
    color: "#FF6B8B",
    roughness: 0.2,
    metalness: 0.08,
  });
  const collarMaterial = new THREE.MeshStandardMaterial({
    color: "#00E5FF",
    emissive: new THREE.Color("#006688"),
    emissiveIntensity: 0.25,
    roughness: 0.15,
    metalness: 0.5,
  });
  const bellGoldMaterial = new THREE.MeshStandardMaterial({
    color: "#FFC800",
    roughness: 0.1,
    metalness: 0.95,
  });
  const mouthDarkCavityMaterial = new THREE.MeshStandardMaterial({
    color: "#9F1239", // Deep ruby mouth cavity
    roughness: 0.3,
  });
  const tongueMaterial = new THREE.MeshStandardMaterial({
    color: "#FB7185", // Cute pink tongue
    roughness: 0.2,
  });

  return (
    <group position={[-0.88, -0.3 + bobY, 0]} rotation={[0, isSpeaking ? 0.12 : 0.32, 0]}>
      {/* Main Body */}
      <group scale={[1 + breathe, 1 - breathe * 0.5, 1 + breathe]}>
        <mesh material={furMaterial} castShadow receiveShadow position={[0, -0.22, 0]} scale={[1.08, 0.98, 1.05]}>
          <sphereGeometry args={[1.0, 36, 36]} />
        </mesh>
        <mesh material={bellyMaterial} castShadow receiveShadow position={[0, -0.25, 0.8]} scale={[0.82, 0.88, 0.32]}>
          <sphereGeometry args={[0.85, 24, 24]} />
        </mesh>
      </group>

      {/* Collar & Golden Bell */}
      <group position={[0, 0.42, 0]} rotation={[0.18, 0, 0]}>
        <mesh material={collarMaterial} castShadow receiveShadow>
          <torusGeometry args={[0.86, 0.1, 16, 36]} />
        </mesh>
        <mesh material={bellGoldMaterial} castShadow receiveShadow position={[0, -0.2, 0.92]}>
          <sphereGeometry args={[0.22, 24, 24]} />
        </mesh>
      </group>

      {/* Head */}
      <group position={[0, 1.18, 0]} rotation={[0, 0, 0.05]}>
        <mesh material={furMaterial} castShadow receiveShadow scale={[1.18, 1.02, 1.1]}>
          <sphereGeometry args={[1.0, 36, 36]} />
        </mesh>

        {/* Ears */}
        <group position={[-0.72, 0.95, -0.05]} rotation={[0.05, 0.1, 0.36 + earTwitchLeft]}>
          <mesh material={furMaterial} castShadow receiveShadow scale={[0.5, 0.9, 0.38]}>
            <coneGeometry args={[1, 1.35, 24]} />
          </mesh>
          <mesh material={innerEarMaterial} position={[0, -0.04, 0.12]} scale={[0.34, 0.68, 0.18]}>
            <coneGeometry args={[1, 1.15, 24]} />
          </mesh>
        </group>
        <group position={[0.72, 0.95, -0.05]} rotation={[0.05, -0.1, -0.36 - earTwitchRight]}>
          <mesh material={furMaterial} castShadow receiveShadow scale={[0.5, 0.9, 0.38]}>
            <coneGeometry args={[1, 1.35, 24]} />
          </mesh>
          <mesh material={innerEarMaterial} position={[0, -0.04, 0.12]} scale={[0.34, 0.68, 0.18]}>
            <coneGeometry args={[1, 1.15, 24]} />
          </mesh>
        </group>

        {/* Eyes */}
        <group position={[-0.42, 0.05, 1.02]} scale={[1, eyeScaleY, 1]}>
          <mesh material={eyeGlossMaterial} castShadow scale={[0.22, 0.26, 0.14]}>
            <sphereGeometry args={[1, 24, 24]} />
          </mesh>
          <mesh material={eyeHighlightMaterial} position={[0.08, 0.08, 0.12]} scale={[0.08, 0.08, 0.04]}>
            <sphereGeometry args={[1, 16, 16]} />
          </mesh>
          <mesh material={eyeHighlightMaterial} position={[-0.04, -0.07, 0.12]} scale={[0.04, 0.04, 0.03]}>
            <sphereGeometry args={[1, 16, 16]} />
          </mesh>
        </group>
        <group position={[0.42, 0.05, 1.02]} scale={[1, eyeScaleY, 1]}>
          <mesh material={eyeGlossMaterial} castShadow scale={[0.22, 0.26, 0.14]}>
            <sphereGeometry args={[1, 24, 24]} />
          </mesh>
          <mesh material={eyeHighlightMaterial} position={[0.08, 0.08, 0.12]} scale={[0.08, 0.08, 0.04]}>
            <sphereGeometry args={[1, 16, 16]} />
          </mesh>
          <mesh material={eyeHighlightMaterial} position={[-0.04, -0.07, 0.12]} scale={[0.04, 0.04, 0.03]}>
            <sphereGeometry args={[1, 16, 16]} />
          </mesh>
        </group>

        {/* Blush Cheeks */}
        <mesh material={blushMaterial} position={[-0.66, -0.15, 0.9]} scale={[0.22, 0.11, 0.05]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
        <mesh material={blushMaterial} position={[0.66, -0.15, 0.9]} scale={[0.22, 0.11, 0.05]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>

        {/* Button Nose */}
        <mesh material={noseMaterial} castShadow position={[0, -0.02, 1.12]} scale={[0.08, 0.055, 0.06]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>

        {/* Dynamic Animated Speaking Mouth (Positioned on the outer surface Z: 1.16) */}
        {isSpeaking ? (
          <group position={[0, -0.16, 1.14]} scale={[1, 0.4 + mouthOpen * 0.9, 1]}>
            {/* Open mouth cavity */}
            <mesh material={mouthDarkCavityMaterial} scale={[0.16, 0.14, 0.06]}>
              <sphereGeometry args={[1, 20, 20]} />
            </mesh>
            {/* Pink tongue inside */}
            <mesh material={tongueMaterial} position={[0, -0.06, 0.03]} scale={[0.1, 0.06, 0.04]}>
              <sphereGeometry args={[1, 16, 16]} />
            </mesh>
          </group>
        ) : (
          <group position={[0, -0.14, 1.12]} rotation={[0, 0, Math.PI]}>
            <mesh material={noseMaterial} position={[-0.07, 0, 0]}>
              <torusGeometry args={[0.07, 0.022, 12, 18, Math.PI * 0.9]} />
            </mesh>
            <mesh material={noseMaterial} position={[0.07, 0, 0]}>
              <torusGeometry args={[0.07, 0.022, 12, 18, Math.PI * 0.9]} />
            </mesh>
          </group>
        )}
      </group>

      {/* Arms */}
      <group position={[-0.92, 0.08, 0.25]} rotation={[0.4, 0, 0.2]}>
        <mesh material={furMaterial} castShadow receiveShadow scale={[0.26, 0.42, 0.26]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
        <mesh material={innerEarMaterial} position={[0, -0.3, 0.06]} scale={[0.15, 0.15, 0.09]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>

      {/* Right Arm (Waving enthusiastically during speech) */}
      <group
        position={[0.95, 0.25, 0.25]}
        rotation={isSpeaking ? [-0.3, 0.1, -0.5 + wave] : [-0.2, 0.2, -0.7 + wave * 0.3]}
      >
        <mesh material={furMaterial} castShadow receiveShadow scale={[0.26, 0.45, 0.26]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
        <mesh material={innerEarMaterial} position={[0, 0.34, 0.04]} scale={[0.16, 0.16, 0.09]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>

      {/* Sitting Feet Planted on Floor */}
      <group position={[-0.52, -1.02, 0.58]} rotation={[-0.2, 0.2, 0]}>
        <mesh material={furMaterial} castShadow receiveShadow scale={[0.34, 0.2, 0.45]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
        <mesh material={innerEarMaterial} position={[0, 0.07, 0.13]} scale={[0.16, 0.05, 0.18]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>
      <group position={[0.52, -1.02, 0.58]} rotation={[-0.2, -0.2, 0]}>
        <mesh material={furMaterial} castShadow receiveShadow scale={[0.34, 0.2, 0.45]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
        <mesh material={innerEarMaterial} position={[0, 0.07, 0.13]} scale={[0.16, 0.05, 0.18]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>

      {/* Fluffy Tail */}
      <group position={[0, -0.45, -0.9]} rotation={[-0.35, tailWag, 0]}>
        <mesh material={furMaterial} castShadow scale={[0.22, 0.32, 0.7]} position={[0, 0.22, -0.22]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
        <mesh material={bellyMaterial} position={[0, 0.46, -0.6]} scale={[0.2, 0.2, 0.2]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>
    </group>
  );
};

// --- PROCEDURAL 3D BOLO (SPEAKER 2 - GROUNDED ON FLOOR) ---
const RoundBallBuddyMesh: React.FC<{
  frame: number;
  fps: number;
  isSpeaking: boolean;
}> = ({ frame, fps, isSpeaking }) => {
  const breathe = Math.sin(frame * 0.12) * 0.025;
  const talkingMouth = isSpeaking ? (0.4 + 0.6 * Math.sin(frame * 0.6)) : 0;
  const handFlapLeft = Math.sin(frame * 0.3) * (isSpeaking ? 0.38 : 0.15);
  const handFlapRight = Math.cos(frame * 0.32) * (isSpeaking ? 0.42 : 0.15);
  const antennaSway = Math.sin(frame * 0.25) * 0.14;

  const blinkCycle = (frame + 35) % 85;
  const isBlinking = blinkCycle > 78 && blinkCycle < 84;
  const eyeScaleY = isBlinking ? 0.08 : 1.0;

  // Materials
  const bodyMaterial = new THREE.MeshStandardMaterial({
    color: "#BA78F8",
    roughness: 0.26,
    metalness: 0.12,
  });
  const bellyCreamMaterial = new THREE.MeshStandardMaterial({
    color: "#F5EDFF",
    roughness: 0.38,
    metalness: 0.02,
  });
  const cheekBlushMaterial = new THREE.MeshStandardMaterial({
    color: "#FF477E",
    emissive: new THREE.Color("#FF0055"),
    emissiveIntensity: 0.4,
    roughness: 0.3,
  });
  const eyeGlossMaterial = new THREE.MeshStandardMaterial({
    color: "#140824",
    roughness: 0.02,
    metalness: 0.3,
  });
  const eyeHighlightMaterial = new THREE.MeshStandardMaterial({
    color: "#FFFFFF",
    roughness: 0.08,
    metalness: 0.0,
  });
  const mouthDarkMaterial = new THREE.MeshStandardMaterial({
    color: "#7E22CE",
    roughness: 0.2,
  });
  const tongueMaterial = new THREE.MeshStandardMaterial({
    color: "#F472B6",
    roughness: 0.2,
  });
  const antennaStemMaterial = new THREE.MeshStandardMaterial({
    color: "#F472B6",
    roughness: 0.18,
    metalness: 0.4,
  });
  const glowingOrbMaterial = new THREE.MeshStandardMaterial({
    color: isSpeaking ? "#FDE047" : "#FBBF24",
    emissive: new THREE.Color(isSpeaking ? "#FEF08A" : "#F59E0B"),
    emissiveIntensity: isSpeaking ? 1.3 : 0.8,
    roughness: 0.1,
    metalness: 0.6,
  });
  const footMaterial = new THREE.MeshStandardMaterial({
    color: "#A855F7",
    roughness: 0.32,
    metalness: 0.1,
  });

  return (
    <group position={[0.92, -0.45, 0.1]} rotation={[0.02, isSpeaking ? -0.1 : -0.35, 0]}>
      {/* Round Ball Body Resting on Floor */}
      <group scale={[1.06 + breathe, 0.94 - breathe * 0.8, 1.04 + breathe]}>
        <mesh material={bodyMaterial} castShadow receiveShadow position={[0, 0, 0]}>
          <sphereGeometry args={[0.92, 40, 40]} />
        </mesh>
        <mesh material={bellyCreamMaterial} castShadow receiveShadow position={[0, -0.12, 0.74]} scale={[0.72, 0.68, 0.3]}>
          <sphereGeometry args={[0.88, 32, 32]} />
        </mesh>
      </group>

      {/* Little Feet Planted on Floor */}
      <group position={[-0.42, -0.72, 0.4]} rotation={[-0.1, 0.2, 0]}>
        <mesh material={footMaterial} castShadow receiveShadow scale={[0.28, 0.16, 0.36]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
        <mesh material={bellyCreamMaterial} position={[0, 0.05, 0.1]} scale={[0.14, 0.05, 0.15]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>
      <group position={[0.42, -0.72, 0.4]} rotation={[-0.1, -0.2, 0]}>
        <mesh material={footMaterial} castShadow receiveShadow scale={[0.28, 0.16, 0.36]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
        <mesh material={bellyCreamMaterial} position={[0, 0.05, 0.1]} scale={[0.14, 0.05, 0.15]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>

      {/* Antenna with Orbiting Saturn Ring */}
      <group position={[0, 0.88, 0]} rotation={[0, 0, antennaSway]}>
        <mesh material={antennaStemMaterial} castShadow position={[0, 0.18, 0]}>
          <cylinderGeometry args={[0.04, 0.05, 0.36, 16]} />
        </mesh>
        <mesh material={glowingOrbMaterial} castShadow position={[0, 0.4, 0]}>
          <sphereGeometry args={[0.15, 24, 24]} />
        </mesh>
        <mesh material={antennaStemMaterial} castShadow position={[0, 0.4, 0]} rotation={[Math.PI / 3, 0, frame * 0.08]}>
          <torusGeometry args={[0.22, 0.02, 12, 24]} />
        </mesh>
      </group>

      {/* Eyes */}
      <group position={[-0.3, 0.12, 0.84]} scale={[1, eyeScaleY, 1]}>
        <mesh material={eyeGlossMaterial} castShadow scale={[0.17, 0.21, 0.11]}>
          <sphereGeometry args={[1, 24, 24]} />
        </mesh>
        <mesh material={eyeHighlightMaterial} position={[-0.05, 0.07, 0.1]} scale={[0.06, 0.06, 0.03]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
        <mesh material={eyeHighlightMaterial} position={[0.05, -0.05, 0.1]} scale={[0.03, 0.03, 0.02]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>
      <group position={[0.3, 0.12, 0.84]} scale={[1, eyeScaleY, 1]}>
        <mesh material={eyeGlossMaterial} castShadow scale={[0.17, 0.21, 0.11]}>
          <sphereGeometry args={[1, 24, 24]} />
        </mesh>
        <mesh material={eyeHighlightMaterial} position={[-0.05, 0.07, 0.1]} scale={[0.06, 0.06, 0.03]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
        <mesh material={eyeHighlightMaterial} position={[0.05, -0.05, 0.1]} scale={[0.03, 0.03, 0.02]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>

      {/* Cheeks */}
      <mesh material={cheekBlushMaterial} position={[-0.52, -0.06, 0.76]} scale={[0.16, 0.09, 0.05]}>
        <sphereGeometry args={[1, 16, 16]} />
      </mesh>
      <mesh material={cheekBlushMaterial} position={[0.52, -0.06, 0.76]} scale={[0.16, 0.09, 0.05]}>
        <sphereGeometry args={[1, 16, 16]} />
      </mesh>

      {/* Dynamic Animated Speaking Mouth (Positioned on the outer surface Z: 0.94) */}
      {isSpeaking ? (
        <group position={[0, -0.08, 0.94]} scale={[1, 0.5 + talkingMouth * 0.9, 1]}>
          <mesh material={mouthDarkMaterial} scale={[0.16, 0.12, 0.05]}>
            <sphereGeometry args={[1, 18, 18]} />
          </mesh>
          <mesh material={tongueMaterial} position={[0, -0.05, 0.02]} scale={[0.1, 0.05, 0.03]}>
            <sphereGeometry args={[1, 16, 16]} />
          </mesh>
        </group>
      ) : (
        <group position={[0, -0.08, 0.92]}>
          <mesh material={mouthDarkMaterial} scale={[0.11, 0.07, 0.04]}>
            <sphereGeometry args={[1, 18, 18]} />
          </mesh>
        </group>
      )}

      {/* Hands */}
      <group position={[-0.86, -0.08, 0.38]} rotation={[0.2, 0, -0.4 + handFlapLeft]}>
        <mesh material={bellyCreamMaterial} castShadow receiveShadow scale={[0.2, 0.2, 0.2]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
      </group>
      <group position={[0.86, -0.08, 0.38]} rotation={[0.2, 0, 0.4 + handFlapRight]}>
        <mesh material={bellyCreamMaterial} castShadow receiveShadow scale={[0.2, 0.2, 0.2]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
      </group>
    </group>
  );
};

// --- REMOTION COMPOSITION: INDO VOICE AI PERKENALAN ---
export const IndoVoiceAIPerkenalan: React.FC = () => {
  const { width, height, fps, durationInFrames } = useVideoConfig();
  const frame = useCurrentFrame();

  // Speech timing windows (Frame-accurate synchronized with Gemini TTS)
  // Speaker 1 (Zui): Frame 25 -> 318
  // Speaker 2 (Bolo): Frame 345 -> 573
  const isZuiSpeaking = frame >= 25 && frame < 318;
  const isBoloSpeaking = frame >= 345 && frame < 573;

  // Cinematic Camera Pan & Focus:
  // Starts centered -> Pans gently towards Zui when Zui speaks -> Pans to Bolo when Bolo speaks -> Centered finale
  const camX = interpolate(
    frame,
    [0, 25, 290, 345, 570, 620],
    [0, -0.5, -0.5, 0.5, 0.5, 0],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );

  const camZ = interpolate(
    frame,
    [0, 25, 290, 345, 570, 620],
    [13.5, 12.0, 12.0, 12.0, 12.0, 13.0],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );

  // Subtitle script timings with clear Speaker Attribution
  const SUBTITLES = [
    {
      from: 25,
      to: 110,
      speakerTag: "ZUI • SPEAKER 1",
      color: "#00E5FF",
      dialogue: "Selamat datang di Indo Voice AI.",
    },
    {
      from: 110,
      to: 318,
      speakerTag: "ZUI • SPEAKER 1",
      color: "#00E5FF",
      dialogue: "Kami berkomitmen untuk menghadirkan teknologi pengolahan suara terbaik bagi industri kreatif Indonesia.",
    },
    {
      from: 345,
      to: 450,
      speakerTag: "BOLO • SPEAKER 2",
      color: "#C084FC",
      dialogue: "Kami percaya bahwa kualitas audio yang mumpuni...",
    },
    {
      from: 450,
      to: 573,
      speakerTag: "BOLO • SPEAKER 2",
      color: "#C084FC",
      dialogue: "...adalah kunci dalam membangun narasi konten yang kuat di era digital ini.",
    },
    {
      from: 573,
      to: 640,
      speakerTag: "INDO VOICE AI",
      color: "#FBBF24",
      dialogue: "Suara Masa Depan Kreator Indonesia • Official Voice Intro",
    },
  ];

  const currentSub = SUBTITLES.find((s) => frame >= s.from && frame < s.to);

  // Stage Materials
  const podiumFloorMaterial = new THREE.MeshStandardMaterial({
    color: "#222430",
    roughness: 0.35,
    metalness: 0.25,
  });
  const podiumRimMaterial = new THREE.MeshStandardMaterial({
    color: "#0F1015",
    roughness: 0.2,
    metalness: 0.5,
  });
  const cyanRingMaterial = new THREE.MeshStandardMaterial({
    color: "#00E5FF",
    emissive: new THREE.Color("#00E5FF"),
    emissiveIntensity: isZuiSpeaking ? 1.4 : 0.6,
    roughness: 0.2,
  });
  const purpleRingMaterial = new THREE.MeshStandardMaterial({
    color: "#C084FC",
    emissive: new THREE.Color("#C084FC"),
    emissiveIntensity: isBoloSpeaking ? 1.4 : 0.6,
    roughness: 0.2,
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#07070A",
        overflow: "hidden",
        fontFamily: "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif",
      }}
    >
      {/* --- AUDIO LAYERS --- */}
      {/* Background Music */}
      <Audio src={staticFile("bgm-clean.mp3")} volume={0.07} loop />

      {/* Speaker 1: Zui (Gemini TTS Kore) starting at frame 25 */}
      <Sequence from={25} durationInFrames={293}>
        <Audio src={staticFile("audio/gemini_speaker1_zui.mp3")} volume={1.0} />
      </Sequence>

      {/* Speaker 2: Bolo (Gemini TTS Enceladus) starting at frame 345 */}
      <Sequence from={345} durationInFrames={228}>
        <Audio src={staticFile("audio/gemini_speaker2_bolo.mp3")} volume={1.0} />
      </Sequence>

      {/* Sound Effects */}
      {frame === 20 && <Audio src={staticFile("click-soft.mp3")} volume={0.4} />}
      {frame === 335 && <Audio src={staticFile("click-soft.mp3")} volume={0.4} />}
      {frame === 573 && <Audio src={staticFile("chime.mp3")} volume={0.6} />}

      {/* AMBIENT SPOTLIGHT GLOW */}
      <div
        style={{
          position: "absolute",
          top: "12%",
          left: "50%",
          transform: "translateX(-50%)",
          width: 980,
          height: 980,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255, 248, 235, 0.12) 0%, rgba(0, 229, 255, 0.08) 35%, rgba(192, 132, 252, 0.08) 60%, transparent 75%)",
          filter: "blur(110px)",
          pointerEvents: "none",
        }}
      />

      {/* --- 3D CANVAS WITH DYNAMIC CAMERA & SHADOWS --- */}
      <div style={{ position: "absolute", width: "100%", height: "100%", zIndex: 10 }}>
        <ThreeCanvas
          shadows
          width={width}
          height={height}
          camera={{
            position: [camX, 0.55, camZ],
            fov: 38,
          }}
          onCreated={({ gl }) => {
            gl.shadowMap.enabled = true;
            gl.shadowMap.type = THREE.PCFSoftShadowMap;
            gl.toneMapping = THREE.ACESFilmicToneMapping;
            gl.toneMappingExposure = 1.15;
          }}
        >
          {/* Deep Ambient */}
          <ambientLight intensity={0.65} color="#2A2A38" />

          {/* Directional Key Light with real shadows */}
          <directionalLight
            castShadow
            position={[-5.5, 9.5, 6.5]}
            intensity={3.8}
            color="#FFF6E8"
            shadow-mapSize={[2048, 2048]}
            shadow-bias={-0.0004}
            shadow-camera-near={0.5}
            shadow-camera-far={25}
            shadow-camera-left={-5.5}
            shadow-camera-right={5.5}
            shadow-camera-top={5.5}
            shadow-camera-bottom={-5.5}
          />

          {/* Fill Light */}
          <directionalLight position={[6, 4, 5]} intensity={1.5} color="#E0E7FF" />

          {/* Dual Rim Backlights */}
          <directionalLight position={[5, 7, -6]} intensity={4.5} color="#00E5FF" />
          <directionalLight position={[-5, 7, -6]} intensity={3.8} color="#C084FC" />

          {/* Stage Platform */}
          <group position={[0, -1.05, 0]}>
            <mesh material={podiumFloorMaterial} receiveShadow position={[0, 0, 0]}>
              <cylinderGeometry args={[3.6, 3.6, 0.12, 48]} />
            </mesh>
            <mesh material={podiumRimMaterial} position={[0, -0.16, 0]}>
              <cylinderGeometry args={[3.7, 3.9, 0.22, 48]} />
            </mesh>
            <mesh material={cyanRingMaterial} position={[-0.88, 0.065, 0]}>
              <ringGeometry args={[1.35, 1.45, 36]} />
            </mesh>
            <mesh material={purpleRingMaterial} position={[0.92, 0.065, 0]}>
              <ringGeometry args={[1.35, 1.45, 36]} />
            </mesh>
          </group>

          {/* Zui (Speaker 1) */}
          <ZuiMesh frame={frame} fps={fps} isSpeaking={isZuiSpeaking} />

          {/* Bolo (Speaker 2) */}
          <RoundBallBuddyMesh frame={frame} fps={fps} isSpeaking={isBoloSpeaking} />
        </ThreeCanvas>
      </div>

      {/* --- 2D OVERLAYS & HUD (TIKTOK SAFE ZONE Y: 220px - 1440px) --- */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 50,
          pointerEvents: "none",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
          paddingTop: 220,
          paddingBottom: 480, // Safe clearance
          boxSizing: "border-box",
        }}
      >
        {/* TOP BRAND LOCKUP HEADER */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
          }}
        >
          {/* Top Brand Pill */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              background: "rgba(18, 18, 24, 0.9)",
              border: "1.5px solid rgba(255, 255, 255, 0.15)",
              borderRadius: 999,
              padding: "10px 28px",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(14px)",
            }}
          >
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: isZuiSpeaking ? "#00E5FF" : isBoloSpeaking ? "#C084FC" : "#FBBF24",
                boxShadow: `0 0 12px ${isZuiSpeaking ? "#00E5FF" : isBoloSpeaking ? "#C084FC" : "#FBBF24"}`,
              }}
            />
            <span
              style={{
                color: "#F4F4F5",
                fontSize: 17,
                fontWeight: 800,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
              }}
            >
              INDO VOICE AI • SUARA RESMI KREATIF
            </span>
          </div>

          {/* Active Speaker Badge Pill */}
          {currentSub && (
            <div
              style={{
                background: "rgba(24, 24, 30, 0.95)",
                border: `2px solid ${currentSub.color}`,
                borderRadius: 14,
                padding: "6px 22px",
                color: currentSub.color,
                fontSize: 16,
                fontWeight: 900,
                letterSpacing: "0.08em",
                boxShadow: `0 0 20px ${currentSub.color}40`,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: currentSub.color,
                }}
              />
              {currentSub.speakerTag}
            </div>
          )}
        </div>

        {/* BOTTOM DYNAMIC SUBTITLE CARD (SAFE ZONE ANCHORED) */}
        {currentSub && (
          <div
            style={{
              width: 880,
              background: "rgba(16, 16, 22, 0.94)",
              border: `2.5px solid ${currentSub.color}66`,
              borderRadius: 28,
              padding: "26px 36px",
              boxShadow: `0 24px 60px rgba(0, 0, 0, 0.8), 0 0 40px ${currentSub.color}25`,
              backdropFilter: "blur(24px)",
              display: "flex",
              flexDirection: "column",
              gap: 12,
              textAlign: "center",
            }}
          >
            {/* Speaker Prefix & Subtitle Line */}
            <div
              style={{
                color: "#FFFFFF",
                fontSize: 32,
                fontWeight: 800,
                lineHeight: 1.35,
                textShadow: "0 4px 16px rgba(0,0,0,0.8)",
              }}
            >
              <span style={{ color: currentSub.color, marginRight: 10 }}>
                [{currentSub.speakerTag.split(" • ")[0]}]:
              </span>
              "{currentSub.dialogue}"
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 16,
                paddingTop: 8,
                borderTop: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              <span style={{ color: "#71717A", fontSize: 14, fontWeight: 700 }}>
                NATURAL AI AUDIO MASTER • EBU R128 (-16 LUFS)
              </span>
            </div>
          </div>
        )}
      </div>
    </AbsoluteFill>
  );
};
